import { useAuthStore } from "~/store/auth/auth.store";
import { useCompaniesStore } from "~/store/companies/companies.store";

/**
 * Single gate for every copilot entry point (header button, floating button, home banner, root
 * panel): company admin only, plus the company's copilot setting.
 *
 * Reads companyInfos — the authenticated company, fetched at login and $reset on Log-As —
 * and never the create/edit form state, which the admin companies modal resets on open and
 * repoints at whichever company is being edited. A failed refresh leaves it null, so access
 * fails closed.
 */
export function useCopilotAccess() {
  const authStore = useAuthStore();
  const companiesStore = useCompaniesStore();

  const canUseCopilot = computed<boolean>(
    () => !!companiesStore.companyInfos?.ocrSignAndSheet && authStore.isCompanyAdmin,
  );

  /**
   * Who is offered "Importer la feuille d'émargement" on a session's steps. Wider than the copilot
   * — the company setting is out, orders never reads it for this — but not everyone who can open
   * the session: orders exposes and asserts `declareAttendance` on `AI_COMPANION_ATTENDANCE_ROLES`
   * (company admin, training plan admin, group admin), while the session detail's admin tab is not
   * role-gated. Offered wider, a manager uploads a sheet, waits out the read and gets a generic
   * failure.
   */
  const canImportAttendanceSheet = computed<boolean>(
    () => authStore.isCompanyAdmin || authStore.isGroupAdmin || authStore.isTrainingPlanAdmin,
  );

  return { canUseCopilot, canImportAttendanceSheet };
}
